import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { authenticatedFetch } from './api';
import './AdminPanel.css';

const SuperAdminPanel = () => {
  const { user, logout } = useAuth();
  const [organizations, setOrganizations] = useState([]);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [activeTab, setActiveTab] = useState('organizations');
  const [formData, setFormData] = useState({
    name: '',
    domain: '',
    allowed_domains: [''],
    description: '',
    user_quota: 0,
    bot_quota: 0
  });

  const normalizeDomainInput = (value) => {
    if (!value) return '';
    let v = String(value).trim().toLowerCase();
    v = v.replace(/^https?:\/\//, '');
    v = v.split('/')[0];
    return v.replace(/\/+$/, '');
  };

  const getNormalizedAllowedDomains = (data) => {
    const fromArray = Array.isArray(data?.allowed_domains) ? data.allowed_domains : [];
    const merged = [...fromArray].map(normalizeDomainInput).filter(Boolean);
    return Array.from(new Set(merged));
  };

  const fetchOrganizations = useCallback(async () => {
    try {
      const response = await authenticatedFetch('/api/super-admin/organizations');
      if (response.ok) {
        const result = await response.json();
        setOrganizations(result.organizations || []);
      } else if (response.status === 403) {
        // If forbidden, user is not a super admin - ProtectedRoute should handle redirect
        console.error('Access forbidden: Super admin role required');
      }
    } catch (error) {
      console.error('Error fetching organizations:', error);
    }
  }, []);

  const defaultStats = () => ({
    total_organizations: 0,
    active_organizations: 0,
    suspended_organizations: 0,
    total_conversations: 0,
    total_files: 0,
    total_admins: 0,
    total_org_admins: 0
  });

  const fetchStats = useCallback(async () => {
    setStatsError(null);
    setStatsLoading(true);
    try {
      const response = await authenticatedFetch('/api/super-admin/stats');
      const result = await response.json().catch(() => ({}));
      if (response.ok && result.success && result.stats) {
        setStats(result.stats);
      } else {
        setStatsError(result.error || result.message || `Failed to load statistics (${response.status})`);
        setStats(prev => prev || defaultStats());
      }
      if (response.status === 403) {
        console.error('Access forbidden: Super admin role required');
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
      setStatsError(error.message || 'Failed to load statistics');
      setStats(prev => prev || defaultStats());
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    // Only load data if user is confirmed to be a super_admin
    // ProtectedRoute handles role checking and redirects, so we can safely assume
    // if we reach here, the user is authenticated as super_admin
    if (user && user.role === 'super_admin') {
      const loadData = async () => {
        setLoading(true);
        await Promise.all([fetchOrganizations(), fetchStats()]);
        setLoading(false);
      };
      loadData();
    }
  }, [user, fetchOrganizations, fetchStats]);

  const handleCreateOrganization = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        allowed_domains: getNormalizedAllowedDomains(formData)
      };
      const response = await authenticatedFetch('/api/super-admin/organizations', {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        setShowCreateModal(false);
        setFormData({ name: '', domain: '', allowed_domains: [''], description: '', user_quota: 0, bot_quota: 0 });
        fetchOrganizations();
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to create organization');
      }
    } catch (error) {
      console.error('Error creating organization:', error);
      alert('Failed to create organization');
    }
  };

  const handleUpdateOrganization = async (orgId) => {
    try {
      const payload = {
        ...formData,
        allowed_domains: getNormalizedAllowedDomains(formData)
      };
      console.log('[SuperAdmin] Updating organization:', orgId, 'with data:', formData);
      const response = await authenticatedFetch(`/api/super-admin/organizations/${orgId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('[SuperAdmin] Organization updated successfully:', result);
        setEditingOrg(null);
        setFormData({ name: '', domain: '', allowed_domains: [''], description: '', user_quota: 0, bot_quota: 0 });
        fetchOrganizations();
        alert('Organization updated successfully!');
      } else {
        const error = await response.json();
        console.error('[SuperAdmin] Failed to update organization:', response.status, error);
        alert(error.error || error.message || 'Failed to update organization');
      }
    } catch (error) {
      console.error('Error updating organization:', error);
      alert('Failed to update organization. Please try again.');
    }
  };

  const handleUpdateStatus = async (orgId, status) => {
    try {
      const response = await authenticatedFetch(`/api/super-admin/organizations/${orgId}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status })
      });
      
      if (response.ok) {
        fetchOrganizations();
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    }
  };

  const handleEditClick = (org) => {
    setEditingOrg(org.id);
    setFormData({
      name: org.name,
      domain: org.domain || '',
      allowed_domains: Array.isArray(org.allowed_domains) && org.allowed_domains.length > 0 ? org.allowed_domains : [org.domain || ''],
      description: org.description || '',
      user_quota: org.user_quota || 0,
      bot_quota: org.bot_quota || 0
    });
  };

  const handleCancelEdit = () => {
    setEditingOrg(null);
    setFormData({ name: '', domain: '', allowed_domains: [''], description: '', user_quota: 0, bot_quota: 0 });
  };

  const handleDeleteOrganization = async (orgId, orgName) => {
    if (!window.confirm(`Are you sure you want to delete "${orgName}"? This will permanently delete the organization and all its data. This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await authenticatedFetch(`/api/super-admin/organizations/${orgId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        fetchOrganizations();
        alert(`Organization "${orgName}" deleted successfully`);
      } else {
        const error = await response.json();
        alert(error.error || error.message || 'Failed to delete organization');
      }
    } catch (error) {
      console.error('Error deleting organization:', error);
      alert('Failed to delete organization');
    }
  };

  const addAllowedDomainRow = () => {
    setFormData((prev) => ({ ...prev, allowed_domains: [...(prev.allowed_domains || []), ''] }));
  };

  const updateAllowedDomainRow = (index, value) => {
    setFormData((prev) => {
      const next = [...(prev.allowed_domains || [])];
      next[index] = value;
      return { ...prev, allowed_domains: next };
    });
  };

  const removeAllowedDomainRow = (index) => {
    setFormData((prev) => {
      const next = (prev.allowed_domains || []).filter((_, i) => i !== index);
      return { ...prev, allowed_domains: next.length > 0 ? next : [''] };
    });
  };

  if (loading) {
    return (
      <div className="admin-panel">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  const userInfo = user && typeof user === 'object' ? user : { username: 'Super Admin' };

  return (
    <div className="admin-panel" style={{ height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <header className="admin-header" style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        flexShrink: 0
      }}>
        <div className="header-left">
          <div className="admin-brand">
            <img className="admin-brand-logo" src={`${process.env.PUBLIC_URL || ''}/assets/wetano-logo.png`} alt="WeTano Solutions" />
            <span className="admin-brand-name" style={{ color: 'white', fontWeight: '800' }}>WeTano Solutions</span>
          </div>
          <h1 style={{ color: 'white', textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>Super Admin Console</h1>
        </div>
        <div className="header-right">
          <span className="welcome-text" style={{ color: 'rgba(255,255,255,0.9)' }}>Welcome, {userInfo.username}!</span>
          <button onClick={logout} className="logout-button" style={{
            background: 'rgba(255,255,255,0.2)',
            border: '1px solid rgba(255,255,255,0.3)',
            color: 'white'
          }}>
            Logout
          </button>
        </div>
      </header>

      <div className="admin-content" style={{ height: 'calc(100vh - 64px)', overflow: 'hidden', display: 'flex', flex: 1 }}>
        <nav className="admin-sidebar" style={{
          background: 'linear-gradient(180deg, #f8f9ff 0%, #ffffff 100%)',
          borderRight: '2px solid #e0e7ff',
          overflowY: 'auto',
          overflowX: 'hidden',
          flexShrink: 0
        }}>
          <ul className="nav-list">
            <li 
              className={`nav-item ${activeTab === 'organizations' ? 'active' : ''}`}
              onClick={() => setActiveTab('organizations')}
              style={{
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                backgroundColor: activeTab === 'organizations' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
                background: activeTab === 'organizations' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
                color: activeTab === 'organizations' ? 'white' : '#4b5563',
                borderRadius: activeTab === 'organizations' ? '8px' : '0',
                margin: activeTab === 'organizations' ? '4px 8px' : '0'
              }}
            >
              <span className="nav-icon">🏢</span>
              <span className="nav-label">Organizations</span>
            </li>
            <li 
              className={`nav-item ${activeTab === 'statistics' ? 'active' : ''}`}
              onClick={() => setActiveTab('statistics')}
              style={{
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                backgroundColor: activeTab === 'statistics' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
                background: activeTab === 'statistics' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
                color: activeTab === 'statistics' ? 'white' : '#4b5563',
                borderRadius: activeTab === 'statistics' ? '8px' : '0',
                margin: activeTab === 'statistics' ? '4px 8px' : '0'
              }}
            >
              <span className="nav-icon">📊</span>
              <span className="nav-label">Statistics</span>
            </li>
          </ul>
        </nav>

        <main className="admin-main" style={{ overflowY: 'auto', overflowX: 'hidden', height: '100%' }}>
          {activeTab === 'statistics' ? (
            /* Statistics View */
            <div className="content-section">
              <div className="section-header">
                <h2>System Statistics</h2>
                <button 
                  className="btn-primary" 
                  onClick={fetchStats}
                  disabled={statsLoading}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 20px',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  <span>🔄</span>
                  {statsLoading ? 'Loading...' : 'Refresh Stats'}
                </button>
              </div>

              {statsError && (
                <div style={{ padding: '12px', marginTop: '1rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#b91c1c' }}>
                  {statsError}
                </div>
              )}

              {statsLoading && !stats && (
                <p style={{ marginTop: '2rem', color: '#6b7280' }}>Loading statistics...</p>
              )}

              {stats && (
                <div className="stats-grid" style={{ marginTop: '2rem' }}>
                  <div className="stat-card" style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white'
                  }}>
                    <h3>Total Organizations</h3>
                    <p className="stat-value" style={{ color: 'white', fontSize: '2.5rem', fontWeight: 'bold' }}>{stats.total_organizations || 0}</p>
                  </div>
                  <div className="stat-card" style={{
                    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                    color: 'white'
                  }}>
                    <h3>Active Organizations</h3>
                    <p className="stat-value" style={{ color: 'white', fontSize: '2.5rem', fontWeight: 'bold' }}>{stats.active_organizations || 0}</p>
                  </div>
                  <div className="stat-card" style={{
                    background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                    color: 'white'
                  }}>
                    <h3>Suspended Organizations</h3>
                    <p className="stat-value" style={{ color: 'white', fontSize: '2.5rem', fontWeight: 'bold' }}>{stats.suspended_organizations || 0}</p>
                  </div>
                  <div className="stat-card" style={{
                    background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                    color: 'white'
                  }}>
                    <h3>Total Conversations</h3>
                    <p className="stat-value" style={{ color: 'white', fontSize: '2.5rem', fontWeight: 'bold' }}>{stats.total_conversations || 0}</p>
                  </div>
                  <div className="stat-card" style={{
                    background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                    color: 'white'
                  }}>
                    <h3>Total Files</h3>
                    <p className="stat-value" style={{ color: 'white', fontSize: '2.5rem', fontWeight: 'bold' }}>{stats.total_files || 0}</p>
                  </div>
                  <div className="stat-card" style={{
                    background: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
                    color: 'white'
                  }}>
                    <h3>Org Admins (Users)</h3>
                    <p className="stat-value" style={{ color: 'white', fontSize: '2.5rem', fontWeight: 'bold' }}>{stats.total_org_admins ?? stats.total_admins ?? 0}</p>
                    <small style={{ opacity: 0.9, fontSize: '12px' }}>Matches users shown under each org</small>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Organizations View */
            <div className="content-section">
          <div className="section-header">
            <h2>Organizations</h2>
            <button 
              className="btn-primary" 
              onClick={() => setShowCreateModal(true)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              <span>➕</span>
              Create Organization
            </button>
          </div>

          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Domain</th>
                  <th>User Quota</th>
                  <th>Bot Quota</th>
                  <th>Status</th>
                  <th>Created At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {organizations.map((org, orgIdx) => (
                  <React.Fragment key={`org-${org.id}-${orgIdx}`}>
                    <tr>
                    <td>
                      {editingOrg === org.id ? (
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="form-input"
                        />
                      ) : (
                        <div>
                          <div style={{ fontWeight: '600', marginBottom: '4px' }}>{org.name}</div>
                          {org.admin_users && org.admin_users.length > 0 && (
                            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                              <div style={{ fontWeight: '500', marginBottom: '2px', color: '#4b5563' }}>Users:</div>
                              {org.admin_users.map((admin, idx) => (
                                <div key={`${org.id}-admin-${admin.id}-${idx}`} style={{ marginLeft: '8px', marginBottom: '2px' }}>
                                  • {admin.username} ({admin.email}) {admin.status !== 'active' && <span style={{ color: '#ef4444', fontSize: '11px' }}>({admin.status})</span>}
                                </div>
                              ))}
                            </div>
                          )}
                          {(!org.admin_users || org.admin_users.length === 0) && (
                            <div style={{ fontSize: '12px', color: '#9ca3af', fontStyle: 'italic', marginTop: '4px' }}>
                              No users assigned
                            </div>
                          )}
                          {org.bots && org.bots.length > 0 && (
                            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
                              <div style={{ fontWeight: '500', marginBottom: '2px', color: '#4b5563' }}>Bots:</div>
                              {org.bots.map((bot, idx) => (
                                <div key={`${org.id}-bot-${bot.id}-${idx}`} style={{ marginLeft: '8px', marginBottom: '2px' }}>
                                  • {bot.bot_name || bot.bot_id} ({bot.bot_id}) {bot.is_enabled ? <span style={{ color: '#166534', fontSize: '11px' }}>enabled</span> : <span style={{ color: '#991b1b', fontSize: '11px' }}>disabled</span>}
                                </div>
                              ))}
                            </div>
                          )}
                          {(!org.bots || org.bots.length === 0) && (
                            <div style={{ fontSize: '12px', color: '#9ca3af', fontStyle: 'italic', marginTop: '8px' }}>
                              No bots
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                    <td>
                      {editingOrg === org.id ? (
                        <div style={{ minWidth: '280px' }}>
                          <input
                            type="text"
                            value={formData.domain}
                            onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                            className="form-input"
                            placeholder="Primary domain"
                            style={{ marginBottom: '8px' }}
                          />
                          {(formData.allowed_domains || []).map((domainValue, idx) => (
                            <div key={`edit-domain-${idx}`} style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                              <input
                                type="text"
                                value={domainValue || ''}
                                onChange={(e) => updateAllowedDomainRow(idx, e.target.value)}
                                className="form-input"
                                placeholder="Allowed domain"
                              />
                              <button type="button" className="btn-small btn-secondary" onClick={() => removeAllowedDomainRow(idx)}>−</button>
                            </div>
                          ))}
                          <button type="button" className="btn-small btn-primary" onClick={addAllowedDomainRow}>+ Domain</button>
                        </div>
                      ) : (
                        <div>
                          <div>{org.domain || '-'}</div>
                          <div style={{ marginTop: '4px', fontSize: '12px', color: '#64748b' }}>
                            {(org.allowed_domains || []).join(', ') || '-'}
                          </div>
                        </div>
                      )}
                    </td>
                    <td>
                      {editingOrg === org.id ? (
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={formData.user_quota ?? ''}
                          onChange={(e) => {
                            const value = e.target.value === '' ? 0 : parseInt(e.target.value) || 0;
                            console.log('[SuperAdmin] User quota changed:', value);
                            setFormData({ ...formData, user_quota: value });
                          }}
                          className="form-input"
                          style={{ width: '80px' }}
                        />
                      ) : (
                        <span style={{ 
                          display: 'inline-flex', 
                          alignItems: 'center',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '13px',
                          fontWeight: '500',
                          backgroundColor: (org.current_users || 0) >= (org.user_quota || 0) ? '#fee2e2' : '#dcfce7',
                          color: (org.current_users || 0) >= (org.user_quota || 0) ? '#991b1b' : '#166534'
                        }}>
                          {org.current_users || 0} / {org.user_quota || 0}
                        </span>
                      )}
                    </td>
                    <td>
                      {editingOrg === org.id ? (
                        <input
                          type="number"
                          min="0"
                          value={formData.bot_quota ?? ''}
                          onChange={(e) => {
                            const value = e.target.value === '' ? 0 : parseInt(e.target.value) || 0;
                            setFormData({ ...formData, bot_quota: value });
                          }}
                          className="form-input"
                          style={{ width: '80px' }}
                        />
                      ) : (
                        <span style={{ 
                          display: 'inline-flex', 
                          alignItems: 'center',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '13px',
                          fontWeight: '500',
                          backgroundColor: (org.current_bots || 0) >= (org.bot_quota || 0) && (org.bot_quota || 0) > 0 ? '#fee2e2' : '#dcfce7',
                          color: (org.current_bots || 0) >= (org.bot_quota || 0) && (org.bot_quota || 0) > 0 ? '#991b1b' : '#166534'
                        }}>
                          {org.current_bots || 0} / {org.bot_quota || 0}
                        </span>
                      )}
                    </td>
                    <td>
                      <select
                        value={org.status}
                        onChange={(e) => handleUpdateStatus(org.id, e.target.value)}
                        className="status-select"
                        style={{
                          padding: '6px 12px',
                          borderRadius: '6px',
                          border: '1px solid #d1d5db',
                          fontSize: '14px',
                          backgroundColor: org.status === 'active' ? '#dcfce7' : '#fee2e2',
                          color: org.status === 'active' ? '#166534' : '#991b1b',
                          fontWeight: '500',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="active">Active</option>
                        <option value="suspended">Suspended</option>
                      </select>
                    </td>
                    <td>
                      {org.created_at ? (
                        <span style={{ fontSize: '14px', color: '#6b7280' }}>
                          {new Date(org.created_at).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'short', 
                            day: 'numeric' 
                          })}
                        </span>
                      ) : '-'}
                    </td>
                    <td>
                      {editingOrg === org.id ? (
                        <div className="action-buttons" style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => handleUpdateOrganization(org.id)}
                            className="btn-small btn-primary"
                            style={{
                              padding: '6px 16px',
                              fontSize: '13px',
                              borderRadius: '6px',
                              border: 'none',
                              cursor: 'pointer',
                              fontWeight: '500',
                              backgroundColor: '#3b82f6',
                              color: '#fff'
                            }}
                          >
                            💾 Save
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="btn-small btn-secondary"
                            style={{
                              padding: '6px 16px',
                              fontSize: '13px',
                              borderRadius: '6px',
                              border: '1px solid #d1d5db',
                              cursor: 'pointer',
                              backgroundColor: '#fff',
                              color: '#374151',
                              fontWeight: '500'
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <button
                            onClick={() => handleEditClick(org)}
                            className="btn-small btn-primary"
                            style={{
                              padding: '6px 16px',
                              fontSize: '13px',
                              borderRadius: '6px',
                              border: 'none',
                              cursor: 'pointer',
                              fontWeight: '500',
                              backgroundColor: '#3b82f6',
                              color: '#fff'
                            }}
                          >
                            ✏️ Edit
                          </button>
                          <button
                            onClick={() => handleDeleteOrganization(org.id, org.name)}
                            className="btn-small btn-danger"
                            style={{
                              padding: '6px 16px',
                              fontSize: '13px',
                              borderRadius: '6px',
                              border: 'none',
                              cursor: 'pointer',
                              fontWeight: '500',
                              backgroundColor: '#ef4444',
                              color: '#fff'
                            }}
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
          )}
        </main>
      </div>

      {/* Create Organization Modal - Full Screen */}
      {showCreateModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            backdropFilter: 'blur(4px)'
          }}
          onClick={() => setShowCreateModal(false)}
        >
          <div 
            style={{
              width: '100%',
              height: '100%',
              maxWidth: '100vw',
              maxHeight: '100vh',
              background: 'white',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.3)',
              animation: 'fadeIn 0.3s ease-out'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              padding: '24px 32px',
              borderBottom: '2px solid #e5e7eb',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexShrink: 0
            }}>
              <h2 style={{
                margin: 0,
                fontSize: '28px',
                fontWeight: '700',
                color: 'white',
                textShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }}>
                Create New Organization
              </h2>
              <button
                onClick={() => setShowCreateModal(false)}
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  color: 'white',
                  fontSize: '32px',
                  fontWeight: '300',
                  width: '40px',
                  height: '40px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                  lineHeight: '1'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.3)';
                  e.target.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.target.style.transform = 'scale(1)';
                }}
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '40px',
              background: '#f9fafb'
            }}>
              <form onSubmit={handleCreateOrganization} style={{ maxWidth: '800px', margin: '0 auto' }}>
                <div style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '32px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)'
                }}>
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#374151'
                    }}>
                      Organization Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: '14px 16px',
                        border: '2px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '16px',
                        transition: 'all 0.2s ease',
                        boxSizing: 'border-box'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#667eea';
                        e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e5e7eb';
                        e.target.style.boxShadow = 'none';
                      }}
                      placeholder="Enter organization name"
                    />
                  </div>

                  <div style={{ marginBottom: '24px' }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#374151'
                    }}>
                      Domain *
                    </label>
                    <input
                      type="text"
                      value={formData.domain}
                      onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                      placeholder="example.com"
                      required
                      style={{
                        width: '100%',
                        padding: '14px 16px',
                        border: '2px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '16px',
                        transition: 'all 0.2s ease',
                        boxSizing: 'border-box'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#667eea';
                        e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e5e7eb';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                    <small style={{
                      display: 'block',
                      marginTop: '6px',
                      color: '#6b7280',
                      fontSize: '13px'
                    }}>
                      Primary organizational domain (legacy field, still used for compatibility)
                    </small>
                  </div>

                  <div style={{ marginBottom: '24px' }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#374151'
                    }}>
                      Allowed Domains
                    </label>
                    {(formData.allowed_domains || []).map((domainValue, idx) => (
                      <div key={`create-domain-${idx}`} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                        <input
                          type="text"
                          value={domainValue || ''}
                          onChange={(e) => updateAllowedDomainRow(idx, e.target.value)}
                          placeholder="example.com"
                          style={{
                            flex: 1,
                            padding: '12px 14px',
                            border: '2px solid #e5e7eb',
                            borderRadius: '8px',
                            fontSize: '15px'
                          }}
                        />
                        <button type="button" onClick={() => removeAllowedDomainRow(idx)} style={{ padding: '0 12px' }}>−</button>
                      </div>
                    ))}
                    <button type="button" onClick={addAllowedDomainRow} style={{ padding: '8px 12px' }}>+ Add Domain</button>
                    <small style={{ display: 'block', marginTop: '6px', color: '#6b7280', fontSize: '13px' }}>
                      These domains are normalized and enforced at runtime for widget/chat API access.
                    </small>
                  </div>

                  <div style={{ marginBottom: '24px' }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#374151'
                    }}>
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows="4"
                      style={{
                        width: '100%',
                        padding: '14px 16px',
                        border: '2px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '16px',
                        fontFamily: 'inherit',
                        resize: 'vertical',
                        transition: 'all 0.2s ease',
                        boxSizing: 'border-box'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#667eea';
                        e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e5e7eb';
                        e.target.style.boxShadow = 'none';
                      }}
                      placeholder="Enter organization description (optional)"
                    />
                  </div>

                  <div style={{ marginBottom: '32px' }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#374151'
                    }}>
                      User Quota (0-100) *
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={formData.user_quota}
                      onChange={(e) => setFormData({ ...formData, user_quota: parseInt(e.target.value) || 0 })}
                      required
                      style={{
                        width: '100%',
                        padding: '14px 16px',
                        border: '2px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '16px',
                        transition: 'all 0.2s ease',
                        boxSizing: 'border-box'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#667eea';
                        e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e5e7eb';
                        e.target.style.boxShadow = 'none';
                      }}
                      placeholder="0"
                    />
                    <small style={{
                      display: 'block',
                      marginTop: '6px',
                      color: '#6b7280',
                      fontSize: '13px'
                    }}>
                      Maximum number of admin users allowed for this organization
                    </small>
                  </div>

                  <div style={{ marginBottom: '32px' }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#374151'
                    }}>
                      Bot Quota (0 = unlimited) *
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.bot_quota}
                      onChange={(e) => setFormData({ ...formData, bot_quota: parseInt(e.target.value) || 0 })}
                      required
                      style={{
                        width: '100%',
                        padding: '14px 16px',
                        border: '2px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '16px',
                        transition: 'all 0.2s ease',
                        boxSizing: 'border-box'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#667eea';
                        e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e5e7eb';
                        e.target.style.boxShadow = 'none';
                      }}
                      placeholder="0"
                    />
                    <small style={{
                      display: 'block',
                      marginTop: '6px',
                      color: '#6b7280',
                      fontSize: '13px'
                    }}>
                      Maximum number of bots this organization can create. Set to 0 for unlimited.
                    </small>
                  </div>

                  {/* Action Buttons */}
                  <div style={{
                    display: 'flex',
                    gap: '16px',
                    justifyContent: 'flex-end',
                    paddingTop: '24px',
                    borderTop: '1px solid #e5e7eb'
                  }}>
                    <button
                      type="button"
                      onClick={() => setShowCreateModal(false)}
                      style={{
                        padding: '12px 24px',
                        fontSize: '16px',
                        fontWeight: '600',
                        borderRadius: '8px',
                        border: '2px solid #d1d5db',
                        background: 'white',
                        color: '#374151',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#f9fafb';
                        e.target.style.borderColor = '#9ca3af';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'white';
                        e.target.style.borderColor = '#d1d5db';
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      style={{
                        padding: '12px 32px',
                        fontSize: '16px',
                        fontWeight: '600',
                        borderRadius: '8px',
                        border: 'none',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 4px 6px rgba(102, 126, 234, 0.3)'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 6px 12px rgba(102, 126, 234, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 6px rgba(102, 126, 234, 0.3)';
                      }}
                    >
                      Create Organization
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminPanel;

